/* eslint-disable no-param-reassign */
import store from 'app/store';
import { QuoteEmailResultsType, QuoteSendStatusType } from 'data/types';
import { setConfidenceGateTelemetry } from 'features/ui';
import axios from 'utils/api';
import { resolveLayoutExistingSystems } from 'utils/ducted-layout-configuration';
import { applyDuctedQuoteLayout } from 'utils/ducted-quote-layout';
import { buildDuctingQuoteSummary } from 'utils/ducting-quote-summary';
import { resolveQuoteCombinedExistingEquipmentSize } from 'utils/existing-equipment-size';
import { fetchZoneAccessoriesBySystem } from 'utils/quotable-system-accessories';
import { buildQuoteFloorplanSnapshotsByConfig } from 'utils/quote-floorplan-snapshot';
import { withSystemDuctRuns } from 'utils/system-duct-run-zones';
import { errorModal } from 'utils/ui';

export async function saveQuoteInfo(
  results: QuoteEmailResultsType,
  currentQuoteIDsToSendSnapshot: string[],
  quoteStatus: QuoteSendStatusType = 'sent'
) {
  const { systemOptions, selectedSystems } = store.getState().systems;
  const { structure, structureUniqueId, existingSystems } =
    store.getState().structure;
  const { instanceUniqueId } = store.getState().project;
  const { configurations } = store.getState().configurations;
  const { ductingKitProducts, airHandlerProducts, ductingComponents } =
    store.getState().data;
  const { zoneDuctModes, confidenceGateTelemetry } = store.getState().ui;
  const floorplanSnapshotsByConfigId = buildQuoteFloorplanSnapshotsByConfig(
    structure,
    configurations,
    zoneDuctModes
  );
  const allOptions = Object.values(systemOptions).flat();
  const maxOptimalDesignScore = Math.max(
    ...allOptions.map((option) => option.optimalDesignScore ?? 1)
  );

  const selectedQuotedSystems = selectedSystems
    .filter((s) => currentQuoteIDsToSendSnapshot.includes(s.uniqueId))
    .map((system) => ({
      ...system,
      configuration: {
        ...system.configuration,
        condensers: system.configuration.condensers.map((c) => ({
          ...c,
          selectedProduct: { ...c.selectedProduct }
        }))
      }
    }));

  const zoneAccessoriesBySystem = await fetchZoneAccessoriesBySystem(
    selectedQuotedSystems.map((system) => ({
      uniqueId: system.uniqueId,
      condensers: system.configuration.condensers.map((condenser) => ({
        condenserUniqueId: condenser.uniqueId,
        zoneUniqueIds: condenser.zones.map((zone) => zone.uniqueId)
      }))
    }))
  );

  const savePromises = results.quotes.map(async (quote) => {
    const system = selectedQuotedSystems.find(
      (s) => s.uniqueId === quote.uniqueId
    );
    if (system) {
      let updatedSystem = { ...system };
      // Ducted (compatibility) systems carry no geometry on their stored
      // configuration; the System Design their pairs were sized on — or,
      // without one, the synthetic layout built from the existing equipment
      // positions — supplies the diagram instead.
      const ductedLayout = applyDuctedQuoteLayout({
        system,
        existingSystems,
        structure,
        configurations,
        zoneDuctModes
      });
      const configurationSnapshot =
        ductedLayout?.floorplanSnapshot ??
        floorplanSnapshotsByConfigId.get(system.configuration.uniqueId);
      const floorplanSnapshot = configurationSnapshot
        ? withSystemDuctRuns(configurationSnapshot, system.configuration)
        : undefined;

      if (selectedQuotedSystems.length === 2) {
        const [system1, system2] = selectedQuotedSystems;

        // Indoor Visibility (Aesthetics Rank)
        if (system1.averageAestheticsRank === system2.averageAestheticsRank) {
          system1.indoorVisibility = 'Less Visible';
          system2.indoorVisibility = 'Less Visible';
        } else if (
          system1.averageAestheticsRank < system2.averageAestheticsRank
        ) {
          system1.indoorVisibility = 'Less Visible';
          system2.indoorVisibility = 'More Visible';
        } else {
          system1.indoorVisibility = 'More Visible';
          system2.indoorVisibility = 'Less Visible';
        }

        // Installation Difficulty
        if (
          system1.installationDifficultyScore ===
          system2.installationDifficultyScore
        ) {
          system1.installationDifficulty = 'Less Difficult';
          system2.installationDifficulty = 'Less Difficult';
        } else if (
          system1.installationDifficultyScore <
          system2.installationDifficultyScore
        ) {
          system1.installationDifficulty = 'Less Difficult';
          system2.installationDifficulty = 'More Difficult';
        } else {
          system1.installationDifficulty = 'More Difficult';
          system2.installationDifficulty = 'Less Difficult';
        }

        // Outdoor Visibility
        const system1OutdoorVolume = system1.configuration.condensers.reduce(
          (total, condenser) =>
            total +
            (condenser.selectedProduct?.dimensions?.width ?? 1) *
              (condenser.selectedProduct?.dimensions?.height ?? 1) *
              (condenser.selectedProduct?.dimensions?.depth ?? 1),
          0
        );

        const system2OutdoorVolume = system2.configuration.condensers.reduce(
          (total, condenser) =>
            total +
            (condenser.selectedProduct?.dimensions?.width ?? 1) *
              (condenser.selectedProduct?.dimensions?.height ?? 1) *
              (condenser.selectedProduct?.dimensions?.depth ?? 1),
          0
        );

        if (system1OutdoorVolume === system2OutdoorVolume) {
          system1.outdoorVisibility = 'Normal Visibility';
          system2.outdoorVisibility = 'Normal Visibility';
        } else if (system1OutdoorVolume < system2OutdoorVolume) {
          system1.outdoorVisibility = 'Less Visible';
          system2.outdoorVisibility = 'More Visible';
        } else {
          system1.outdoorVisibility = 'More Visible';
          system2.outdoorVisibility = 'Less Visible';
        }

        // Assign the updated system
        if (updatedSystem.uniqueId === system1.uniqueId) {
          updatedSystem = { ...updatedSystem, ...system1 };
        } else if (updatedSystem.uniqueId === system2.uniqueId) {
          updatedSystem = { ...updatedSystem, ...system2 };
        }
      }

      if (selectedQuotedSystems.length === 3) {
        // Indoor Visibility (Aesthetics Rank)
        const sortedByAesthetics = [...selectedQuotedSystems].sort(
          (a, b) => a.averageAestheticsRank - b.averageAestheticsRank
        );
        if (
          sortedByAesthetics[0].averageAestheticsRank ===
          sortedByAesthetics[2].averageAestheticsRank
        ) {
          // All 3 systems are tied
          sortedByAesthetics.forEach((sys) => {
            sys.indoorVisibility = 'Less Visible';
          });
        } else if (
          sortedByAesthetics[0].averageAestheticsRank ===
          sortedByAesthetics[1].averageAestheticsRank
        ) {
          sortedByAesthetics[0].indoorVisibility = 'Less Visible';
          sortedByAesthetics[1].indoorVisibility = 'Less Visible';
          sortedByAesthetics[2].indoorVisibility = 'More Visible';
        } else if (
          sortedByAesthetics[1].averageAestheticsRank ===
          sortedByAesthetics[2].averageAestheticsRank
        ) {
          sortedByAesthetics[0].indoorVisibility = 'Least Visible';
          sortedByAesthetics[1].indoorVisibility = 'More Visible';
          sortedByAesthetics[2].indoorVisibility = 'More Visible';
        } else {
          sortedByAesthetics[0].indoorVisibility = 'Least Visible';
          sortedByAesthetics[1].indoorVisibility = 'Less Visible';
          sortedByAesthetics[2].indoorVisibility = 'Most Visible';
        }

        // Installation Difficulty
        const sortedByDifficulty = [...selectedQuotedSystems].sort(
          (a, b) =>
            a.installationDifficultyScore - b.installationDifficultyScore
        );
        if (
          sortedByDifficulty[0].installationDifficultyScore ===
          sortedByDifficulty[2].installationDifficultyScore
        ) {
          // All 3 systems are tied
          sortedByDifficulty.forEach((sys) => {
            sys.installationDifficulty = 'Less Difficult';
          });
        } else if (
          sortedByDifficulty[0].installationDifficultyScore ===
          sortedByDifficulty[1].installationDifficultyScore
        ) {
          sortedByDifficulty[0].installationDifficulty = 'Less Difficult';
          sortedByDifficulty[1].installationDifficulty = 'Less Difficult';
          sortedByDifficulty[2].installationDifficulty = 'More Difficult';
        } else if (
          sortedByDifficulty[1].installationDifficultyScore ===
          sortedByDifficulty[2].installationDifficultyScore
        ) {
          sortedByDifficulty[0].installationDifficulty = 'Least Difficult';
          sortedByDifficulty[1].installationDifficulty = 'More Difficult';
          sortedByDifficulty[2].installationDifficulty = 'More Difficult';
        } else {
          sortedByDifficulty[0].installationDifficulty = 'Least Difficult';
          sortedByDifficulty[1].installationDifficulty = 'Less Difficult';
          sortedByDifficulty[2].installationDifficulty = 'Most Difficult';
        }

        // Outdoor Visibility
        const sortedByOutdoorVisibility = [...selectedQuotedSystems].sort(
          (a, b) => {
            const aVolume = a.configuration.condensers.reduce(
              (total, condenser) =>
                total +
                (condenser.selectedProduct?.dimensions?.width ?? 1) *
                  (condenser.selectedProduct?.dimensions?.height ?? 1) *
                  (condenser.selectedProduct?.dimensions?.depth ?? 1),
              0
            );

            const bVolume = b.configuration.condensers.reduce(
              (total, condenser) =>
                total +
                (condenser.selectedProduct?.dimensions?.width ?? 1) *
                  (condenser.selectedProduct?.dimensions?.height ?? 1) *
                  (condenser.selectedProduct?.dimensions?.depth ?? 1),
              0
            );

            return aVolume - bVolume;
          }
        );
        if (
          sortedByOutdoorVisibility[0].configuration.condensers ===
          sortedByOutdoorVisibility[2].configuration.condensers
        ) {
          // All 3 systems are tied
          sortedByOutdoorVisibility.forEach((sys) => {
            sys.outdoorVisibility = 'Normal Visibility';
          });
        } else if (
          sortedByOutdoorVisibility[0].configuration.condensers ===
          sortedByOutdoorVisibility[1].configuration.condensers
        ) {
          sortedByOutdoorVisibility[0].outdoorVisibility = 'Less Visible';
          sortedByOutdoorVisibility[1].outdoorVisibility = 'Less Visible';
          sortedByOutdoorVisibility[2].outdoorVisibility = 'More Visible';
        } else if (
          sortedByOutdoorVisibility[1].configuration.condensers ===
          sortedByOutdoorVisibility[2].configuration.condensers
        ) {
          sortedByOutdoorVisibility[0].outdoorVisibility = 'Least Visible';
          sortedByOutdoorVisibility[1].outdoorVisibility = 'More Visible';
          sortedByOutdoorVisibility[2].outdoorVisibility = 'More Visible';
        } else {
          sortedByOutdoorVisibility[0].outdoorVisibility = 'Least Visible';
          sortedByOutdoorVisibility[1].outdoorVisibility = 'Less Visible';
          sortedByOutdoorVisibility[2].outdoorVisibility = 'Most Visible';
        }

        // Assign the updated system back
        updatedSystem = {
          ...updatedSystem,
          ...sortedByAesthetics.find((s) => s.uniqueId === system.uniqueId)
        };
        updatedSystem = {
          ...updatedSystem,
          ...sortedByDifficulty.find((s) => s.uniqueId === system.uniqueId)
        };
        updatedSystem = {
          ...updatedSystem,
          ...sortedByOutdoorVisibility.find(
            (s) => s.uniqueId === system.uniqueId
          )
        };
      }

      updatedSystem = {
        ...updatedSystem,
        optimalDesignScore: Math.floor(
          ((updatedSystem.optimalDesignScore ?? 1) / maxOptimalDesignScore) * 10
        )
      };

      const ductingSummary = buildDuctingQuoteSummary({
        system: updatedSystem,
        configurations,
        ductingKitProducts,
        airHandlerProducts,
        ductingComponents,
        zoneDuctModes,
        quotedAccessoriesByZone: zoneAccessoriesBySystem[updatedSystem.uniqueId]
      });

      const quoteConfiguration =
        ductedLayout?.configuration ?? updatedSystem.configuration;

      const existingEquipmentSize = resolveQuoteCombinedExistingEquipmentSize({
        configuration: quoteConfiguration,
        existingSystems: resolveLayoutExistingSystems(system, existingSystems)
      });

      try {
        await axios.post('quote-data', {
          quoteId: quote.quoteId,
          quoteData: {
            ...updatedSystem,
            quoteStatus,
            selectedAccessories: [],
            configuration: {
              ...quoteConfiguration,
              condensers: quoteConfiguration.condensers.map((c) => ({
                ...c,
                selectedAccessories: [],
                accessoriesCompleted: [],
                accessoriesNotNeeded: [],
                noAccessoriesNeeded: undefined,
                selectedProduct: {
                  ...c.selectedProduct,
                  accessories: []
                },
                zones: c.zones.map((z) => ({
                  ...z,
                  selectedProduct: {
                    ...z.selectedProduct,
                    accessories: []
                  }
                }))
              }))
            },
            shipping: undefined,
            tax: undefined,
            totalRebates: undefined,
            rebates: undefined,
            inStock: undefined,
            restockDate: undefined,
            floorplanSnapshot,
            ductingSummary:
              ductingSummary.length > 0 ? ductingSummary : undefined,
            ...(existingEquipmentSize ? { existingEquipmentSize } : {}),
            // AHA-6796 gate telemetry: present only when a notify-mode
            // confidence gate was continued past for this finalization.
            ...(confidenceGateTelemetry
              ? { confidenceGate: confidenceGateTelemetry }
              : {})
          },
          structureUniqueId,
          quotableSystemUniqueId: system.uniqueId,
          instanceUniqueId
        });
      } catch (error) {
        console.error(error);
        errorModal('Failed to save quote data');
      }
    } else {
      console.error(`Cannot find system ${quote.uniqueId}`);
      errorModal('Failed to save quote data');
    }
  });

  await Promise.all(savePromises);

  // The stamp covered this save batch — clear it so it never rides a later
  // finalization.
  if (confidenceGateTelemetry) {
    store.dispatch(setConfidenceGateTelemetry(null));
  }
}
